import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Collapse, Row } from 'antd'
import { Formik, FormikProps } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'

import { ApplicationInfo, Plan } from 'core/application/ichraDecisionTool/prospectSignup/state'
import question5Api, { Question5Values } from 'core/application/ichraDecisionTool/prospectSignup/step2/question5'

import Tooltip from 'common/components/dataDisplay/Tooltip'
import Checkbox from 'common/components/dataEntry/components/Checkbox'
import FormikCheckboxGroupField from 'common/components/dataEntry/formik/FormikCheckboxGroupField'
import FormikDatePickerField from 'common/components/dataEntry/formik/FormikDatePickerField'
import Input from 'common/components/dataEntry/formik/FormikDefaultInputField'
import Radio from 'common/components/dataEntry/formik/FormikRadioField'
import notification from 'common/components/feedback/Notification'
import Button from 'common/components/general/Button'

import { commandRequest } from 'common/RequestUtils'
import { BaseNavigationProps } from 'common/wizard/BaseNavigationProps'

import CategoryItemPageHeader from '../common/CategoryItemPageHeader'

import './Step2.less'

const { Panel } = Collapse

const DEFAULT_ACTIVE_KEY = 1
const INDIVIDUAL_ID = 0
const FAMILY_ID = 1

const formValidator = (translate: any) =>
  Yup.object({
    eligibleEmployeesNumber: Yup.number().required(translate('required')).nullable().min(1),
    participatingEmployeesNumber: Yup.number().required(translate('required')).nullable().min(1),
    carrier: Yup.string().trim().nullable().required(translate('required')),
    planMetalTier: Yup.number().required(translate('required')),

    deductible: Yup.array().min(1, translate('at-least-one')).required(translate('required')),
    deductibleIndividualAmount: Yup.number()
      .required(translate('required'))
      .nullable()
      .min(1, translate('greater-than-one')),
    deductibleFamilyAmount: Yup.number()
      .nullable()
      .notRequired()
      .when('deductible', {
        is: (deductible: number[]) => deductible.includes(FAMILY_ID),
        then: Yup.number().nullable().required(translate('required')).min(1, translate('greater-than-one')),
      }),

    maxOutOfPocket: Yup.array().min(1, translate('greater-than-one')).required(translate('required')),
    maxOutOfPocketIndividualAmount: Yup.number()
      .required(translate('required'))
      .nullable()
      .min(1, translate('greater-than-one')),
    maxOutOfPocketFamilyAmount: Yup.number()
      .nullable()
      .notRequired()
      .when('maxOutOfPocket', {
        is: (maxOutOfPocket: number[]) => maxOutOfPocket.includes(FAMILY_ID),
        then: Yup.number().nullable().required(translate('required')).min(1, translate('greater-than-one')),
      }),

    organizationSpendPerMonth: Yup.number()
      .required(translate('required'))
      .nullable()
      .min(1, translate('greater-than-one')),
    employeeCoveragePercentage: Yup.number()
      .required(translate('required'))
      .nullable()
      .min(1, translate('between-1-and-100'))
      .max(100, translate('between-1-and-100')),
    renewalOrStartDate: Yup.date().required(translate('required')).typeError('Required').nullable(),
    rateIncrease: Yup.string().notRequired().nullable(),
  })

export type Step2Question5Props = Omit<
  Plan,
  | 'budgetGoal'
  | 'budgetGoalsOptions'
  | 'employeeContributionStrategyOptions'
  | 'availableReimbursementOptions'
  | 'employeeCategoryOptions'
> &
  Pick<ApplicationInfo, 'currentPlanStatus'>

const Question5: React.FC<BaseNavigationProps<Step2Question5Props>> = (props) => {
  const { t: translate } = useTranslation('ichra-decision-tool')
  const [loading, setLoading] = useState<boolean>(false)

  const onSubmit = async (values: Step2Question5Props) => {
    setLoading(true)

    const apiValues: Question5Values = {
      carrier: values.carrier,
      deductible: values.deductible,
      deductibleIndividualAmount: values.deductibleIndividualAmount ? +values.deductibleIndividualAmount : null,
      deductibleFamilyAmount:
        values.deductibleFamilyAmount && values.deductible.includes(FAMILY_ID) ? +values.deductibleFamilyAmount : null,
      id: values.id,
      planMetalTier: values.planMetalTier,
      maxOutOfPocket: values.maxOutOfPocket,
      maxOutOfPocketIndividualAmount: values.maxOutOfPocketIndividualAmount
        ? +values.maxOutOfPocketIndividualAmount
        : null,
      maxOutOfPocketFamilyAmount:
        values.maxOutOfPocketFamilyAmount && values.maxOutOfPocket.includes(FAMILY_ID)
          ? +values.maxOutOfPocketFamilyAmount
          : null,
      renewalOrStartDate: values.renewalOrStartDate,
      rateIncrease: values.rateIncrease ? +values.rateIncrease : null,
      dependentsCoveragePercentage: values.dependentsCoveragePercentage ? +values.dependentsCoveragePercentage : null,
      eligibleEmployeesNumber: +values.eligibleEmployeesNumber,
      employeeCoveragePercentage: +values.employeeCoveragePercentage,
      organizationSpendPerMonth: +values.organizationSpendPerMonth,
      participatingEmployeesNumber: +values.participatingEmployeesNumber,
    }

    const res = await commandRequest(() => question5Api(apiValues))
    setLoading(false)
    if (!res.errors) {
      props.goNext(props.name)
    } else {
      notification.open({
        message: res.errors.join(','),
        type: 'error',
      })
    }
  }

  const getNextButton = (formik: FormikProps<Step2Question5Props>): ReactNode => {
    const nextButton = (
      <Button
        icon="fa fa-arrow-right"
        iconPosition="right"
        size="large"
        htmlType="submit"
        type="primary"
        disabled={
          !formValidator(translate).isValidSync(formik.values) ||
          !checkIfValidDependentsCoverage(formik.values.dependentsCoveragePercentage)
        }
        className="float-right next__button"
        loading={loading}
      >
        {translate('next')}
      </Button>
    )

    const missingInput =
      !formValidator(translate).isValidSync(formik.values) ||
      !checkIfValidDependentsCoverage(formik.values.dependentsCoveragePercentage)

    return missingInput ? (
      <Tooltip title={translate('next-tooltip')} position="left">
        {nextButton}
      </Tooltip>
    ) : (
      nextButton
    )
  }

  const planTitle = (
    <div>
      <h5>{translate('plan-number')}</h5>
    </div>
  )

  const checkIfValidDependentsCoverage = (dependentsCoveragePercentage: number | null | undefined): boolean => {
    return dependentsCoveragePercentage
      ? dependentsCoveragePercentage >= 0 && dependentsCoveragePercentage <= 100
      : true
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={props.state}
      validationSchema={formValidator(translate)}
      onSubmit={onSubmit}
      validate={(values) => {
        props.hasDirtyData(true)
        if (!checkIfValidDependentsCoverage(values.dependentsCoveragePercentage)) {
          return { dependentsCoveragePercentage: translate('between-0-and-100') }
        }
      }}
    >
      {(formik) => (
        <form onSubmit={formik.handleSubmit} autoComplete="off" onBlur={formik.handleBlur}>
          <Row>
            <Col xs={24} sm={24} md={13}>
              <CategoryItemPageHeader
                title={translate('prospect-signup.step-2.question-1-title')}
                numberOfQuestions={6}
                currentQuestion={5}
                question={translate('prospect-signup.step-2.question-5.question').replace(
                  '[]',
                  props.state.currentPlanStatus === 0 ? translate('plan') : translate('quote'),
                )}
                className="ml-0"
                titleFontSize={20}
              />
            </Col>
          </Row>

          <Row className="mt-16">
            <Col xs={24} sm={24} md={12}>
              <Collapse activeKey={DEFAULT_ACTIVE_KEY}>
                <Panel showArrow={false} header={planTitle} key={DEFAULT_ACTIVE_KEY}>
                  <Row>
                    <Col xs={24} sm={24} md={24}>
                      <Row>
                        <Col>
                          <Input
                            type="number"
                            name="eligibleEmployeesNumber"
                            label={translate('prospect-signup.step-2.question-5.nr-of-ee')}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Input
                            type="number"
                            name="participatingEmployeesNumber"
                            label={translate('prospect-signup.step-2.question-5.nr-of-participating-ee')}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Input
                            name="carrier"
                            label={translate('prospect-signup.step-2.question-5.carrier-provider')}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Radio.Group
                          name="planMetalTier"
                          defaultValue={props.state.planMetalTier}
                          label={translate('prospect-signup.step-2.question-5.plan-tier')}
                        >
                          <Row>
                            {props.state.planMetalTiersOptions.map((option) => (
                              <Col className="flex__space_between" md={12}>
                                <div className="full-width">
                                  <Radio.Button value={option.id}>{option.name}</Radio.Button>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </Radio.Group>
                      </Row>
                      <Row className="mt-16">
                        <Col md={24}>
                          <FormikCheckboxGroupField
                            defaultValue={props.state.deductible}
                            name="deductible"
                            className="full-width"
                            label={translate('prospect-signup.step-2.question-5.deductible')}
                          >
                            <div className="flex">
                              {props.state.deductibleOptions.map((option, index) => (
                                <div className="full-width  option">
                                  <Checkbox
                                    value={option.id}
                                    key={option.id}
                                    white
                                    className="category_element"
                                    disabled={index == INDIVIDUAL_ID}
                                  >
                                    {option.name}
                                  </Checkbox>
                                </div>
                              ))}
                            </div>
                            <Row>
                              <Col span={12} className="pr-8 mt-8">
                                <Input type="number" name="deductibleIndividualAmount" prefix={'$'} min={1} />
                              </Col>
                              <Col span={12} className="pr-8 mt-8">
                                {formik.values.deductible.includes(FAMILY_ID) && (
                                  <Input type="number" name="deductibleFamilyAmount" prefix={'$'} min={1} />
                                )}
                              </Col>
                            </Row>
                          </FormikCheckboxGroupField>
                        </Col>
                      </Row>

                      <Row className="mt-16">
                        <Col md={24}>
                          <FormikCheckboxGroupField
                            defaultValue={props.state.maxOutOfPocket}
                            name="maxOutOfPocket"
                            className="full-width"
                            label={translate('prospect-signup.step-2.question-5.max-out-of-pocket')}
                          >
                            <div className="flex">
                              {props.state.maxOutOfPocketOptions.map((option, index) => (
                                <div className="full-width  option">
                                  <Checkbox
                                    value={option.id}
                                    key={option.id}
                                    white
                                    className="category_element"
                                    disabled={index == INDIVIDUAL_ID}
                                  >
                                    {option.name}
                                  </Checkbox>
                                </div>
                              ))}
                            </div>
                            <Row>
                              <Col span={12} className="pr-8 mt-8">
                                <Input type="number" name="maxOutOfPocketIndividualAmount" prefix={'$'} min={1} />
                              </Col>
                              <Col span={12} className="pr-8 mt-8">
                                {formik.values.maxOutOfPocket.includes(FAMILY_ID) && (
                                  <Input type="number" name="maxOutOfPocketFamilyAmount" prefix={'$'} min={1} />
                                )}
                              </Col>
                            </Row>
                          </FormikCheckboxGroupField>
                        </Col>
                      </Row>

                      <Row className="mt-16">
                        <Col>
                          <Input
                            type="number"
                            name="organizationSpendPerMonth"
                            label={translate('prospect-signup.step-2.question-5.insurance-premiums-per-month')}
                            prefix={'$'}
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col>
                          <Input
                            type="number"
                            name="employeeCoveragePercentage"
                            label={translate('prospect-signup.step-2.question-5.percentage-employee-cover')}
                            prefix={'%'}
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col>
                          <Input
                            type="number"
                            name="dependentsCoveragePercentage"
                            label={translate('prospect-signup.step-2.question-5.percentage-dependents-cover')}
                            prefix={'%'}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <FormikDatePickerField
                            label={
                              props.state.currentPlanStatus === 0
                                ? translate('prospect-signup.step-2.question-5.scheduled-renewal-date')
                                : translate('prospect-signup.step-2.question-5.potential-renewal-date')
                            }
                            name="renewalOrStartDate"
                            id="renewalOrStartDate"
                            noStyles
                            defaultValue={
                              props.state.renewalOrStartDate ? moment(props.state.renewalOrStartDate) : null
                            }
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col>
                          <Input
                            type="number"
                            name="rateIncrease"
                            label={translate('prospect-signup.step-2.question-5.rate-increase')}
                            prefix={'%'}
                            min={1}
                            max={100}
                            hint={translate('(optional)')}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Panel>
              </Collapse>
            </Col>
          </Row>

          <Row>
            <Col xs={24}>
              <div className="bottom__row mt-16">
                <Button
                  icon="fa fa-arrow-left"
                  size="large"
                  htmlType="button"
                  type="default"
                  onClick={() => {
                    props.goBack(props.name)
                  }}
                >
                  {translate('back')}
                </Button>
                {getNextButton(formik)}
              </div>
            </Col>
          </Row>
        </form>
      )}
    </Formik>
  )
}

export default Question5
